import type { CSSProperties, FC, ReactNode } from 'react'

import { Bot, CircleCheck, CornerDownRight, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FloatingCard, PreviewFrame } from '@/components/features/preview-frame'

const STEPS = [
    { keyword: 'When', text: 'a transaction arrives from', value: 'Amazon Web Services' },
    { keyword: 'Then', text: 'categorize it as', value: 'Software & cloud' },
    { keyword: 'And', text: 'match the receipt from', value: 'Billing inbox' },
    { keyword: 'If', text: 'the amount is above', value: '$10,000' },
    { keyword: 'Then', text: 'ask for approval from', value: 'Finance lead' }
]

const ACTIVITY = [
    { text: 'AWS, $12,480.00 categorized and matched', time: '2 min ago' },
    { text: 'Approval requested for AWS invoice #4471', time: '2 min ago' },
    { text: 'Figma, $1,350.00 matched to receipt', time: '1 hr ago' }
]

/** Rule builder preview showing how an automation is composed. */
export const AutomationFlow: FC = (): ReactNode => {
    return (
        <div className='relative'>
            <PreviewFrame>
                <div className='flex items-start justify-between gap-3'>
                    <div className='flex items-center gap-3'>
                        <span className='flex size-9 items-center justify-center rounded-xl bg-burgundy text-gold'>
                            <Zap aria-hidden='true' className='size-4' />
                        </span>

                        <div>
                            <p className='text-sm font-medium'>Cloud spend automation</p>
                            <p className='text-[11px] text-stone'>Active, ran 412 times this month</p>
                        </div>
                    </div>

                    <span className='inline-flex items-center gap-1.5 rounded-full bg-sage/20 px-2.5 py-1 text-[11px] font-medium text-sage-dark'>
                        <span className='size-1.5 rounded-full bg-sage-dark' />
                        On
                    </span>
                </div>

                <ol className='relative mt-6 space-y-2.5' data-chart=''>
                    <span aria-hidden='true' className='absolute bottom-4 left-[19px] top-4 w-px bg-burgundy/10' />

                    {STEPS.map((step, index) => (
                        <li
                            key={`${step.keyword}-${index}`}
                            className='chart-fade relative flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-burgundy/[0.07] bg-ivory py-2.5 pl-12 pr-3 text-xs'
                            style={{ '--chart-delay': `${index * 180}ms` } as CSSProperties}
                        >
                            <span className={cn(
                                'absolute left-2.5 top-1/2 flex h-6 min-w-6 -translate-y-1/2 items-center justify-center rounded-md px-1 font-mono text-[9px] font-medium uppercase',
                                index === 0 ? 'bg-burgundy text-cream' : 'bg-cream text-stone-dark'
                            )}>
                                {step.keyword}
                            </span>

                            <span className='text-stone-dark'>{step.text}</span>
                            <span className='rounded-md bg-gold/20 px-2 py-0.5 font-medium text-burgundy'>{step.value}</span>
                        </li>
                    ))}
                </ol>

                <div className='mt-5 border-t border-burgundy/[0.06] pt-4'>
                    <p className='font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>Recent activity</p>

                    <ul className='mt-3 space-y-2'>
                        {ACTIVITY.map(item => (
                            <li key={item.text} className='flex items-center gap-2 text-[11px] text-stone-dark'>
                                <CornerDownRight aria-hidden='true' className='size-3 shrink-0 text-stone' />
                                <span className='flex-1'>{item.text}</span>
                                <span className='whitespace-nowrap text-stone'>{item.time}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </PreviewFrame>

            <FloatingCard className='-right-4 -top-8 w-56 lg:-right-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <Bot aria-hidden='true' className='size-4 text-gold-dark' />
                    Built from a sentence
                </p>

                <p className='mt-1 text-[11px] text-stone'>&ldquo;Send big AWS bills to me for approval&rdquo;</p>
            </FloatingCard>

            <FloatingCard slow className='-bottom-7 -left-4 w-52 lg:-left-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <CircleCheck aria-hidden='true' className='size-4 text-sage-dark' />
                    38 hours saved
                </p>

                <p className='mt-1 text-[11px] text-stone'>Across 9 automations this month</p>
            </FloatingCard>
        </div>
    )
}
